import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import {
  Share as ShareIcon,
  ChevronRight as ChevronRightIcon,
  Description as DescriptionIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { API, Storage } from 'aws-amplify';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import { GamifyToast } from '../../../common/CustomToasts';
import * as mutations from '../../../../graphql/mutations';
import * as queries from '../../../../graphql/queries';
import ReportViewModal from './ReportViewModal';

interface DealReportsProps {
  dealID: string;
  dealInfo: any;
}

interface Report {
  id: string;
  url: string;
  description?: string;
  createdAt: string;
  version?: string;
}

interface ReportData {
  dealId: string;
  title: string;
  version: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  author: any;
  company: any;
  property: any;
  mainImage: any;
  sections: any[];
}

const DealReports: React.FC<DealReportsProps> = ({ dealID, dealInfo }) => {
  const { theme } = useTheme();
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [showReportView, setShowReportView] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);

  const [shareData, setShareData] = useState({
    sendToPhone: false,
    sendToEmail: false,
    phoneNumber: '',
    email: '',
  });

  const customerContacts = useMemo(() => {
    if (!dealInfo) return { phone: '', email: '' };

    try {
      const flexAttrs = dealInfo.flexAttributes
        ? JSON.parse(dealInfo.flexAttributes)
        : {};

      return {
        phone: flexAttrs['Phone Number'] || flexAttrs.phone || '',
        email: flexAttrs['Email Address'] || flexAttrs.email || '',
      };
    } catch {
      return { phone: '', email: '' };
    }
  }, [dealInfo]);

  const fetchReports = useCallback(async () => {
    if (!dealID) return;

    setIsLoading(true);
    setError(null);

    try {
      // @ts-ignore - Get orgID from user or fallback to deal
      const orgId = global?.me?.orgID || dealInfo?.orgID;
      if (!orgId) {
        console.warn(
          'Organization ID not available, fetching without org filter'
        );
      }

      const filter: any = { isDeleted: { ne: true } };
      if (orgId) {
        filter.orgID = { eq: orgId };
      }

      const result: any = await API.graphql({
        query: queries.listDealFilesByDealIDType,
        variables: {
          dealID,
          type: { eq: 'report' },
          filter,
        },
      });

      const files = result?.data?.listDealFilesByDealIDType?.items || [];

      const reportFiles = files.filter((file: any) => file && !file.isDeleted);

      setReports(reportFiles);
    } catch (err: any) {
      console.error('Error fetching reports:', err);
      setError(err.message || 'Failed to load reports');
    } finally {
      setIsLoading(false);
    }
  }, [dealID]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleViewReport = async (report: Report) => {
    try {
      setIsLoading(true);
      setSelectedReport(report);

      // Fetch the JSON report data from S3
      const reportJson = await Storage.get(report.url, { download: true });

      if (reportJson && (reportJson as any).Body) {
        const text = await (reportJson as any).Body.text();
        const parsedReport = JSON.parse(text) as ReportData;
        setReportData(parsedReport);
        setShowReportView(true);
      } else {
        GamifyToast.error('Failed to load report data');
      }
    } catch (err) {
      console.error('Error opening report:', err);
      GamifyToast.error('Failed to open report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportReport = (report: Report) => {
    setSelectedReport(report);
    setShareData({
      sendToPhone: false,
      sendToEmail: false,
      phoneNumber: customerContacts.phone,
      email: customerContacts.email,
    });
    setShowShareModal(true);
  };

  const handleShareSend = async () => {
    if (!selectedReport) return;

    if (!shareData.sendToPhone && !shareData.sendToEmail) {
      GamifyToast.error('Please select at least one delivery method');
      return;
    }

    try {
      setIsSendingReport(true);

      const reportID = selectedReport.id;
      const sendRequest: any = {
        reportID,
        dealID,
        contactName: dealInfo?.name || dealInfo?.customerName || '',
      };

      if (shareData.sendToPhone && shareData.phoneNumber) {
        sendRequest.phoneNumber = shareData.phoneNumber;
      }
      if (shareData.sendToEmail && shareData.email) {
        sendRequest.emailAddress = shareData.email;
      }

      await API.graphql({
        query: mutations.sendDamageReport,
        variables: { request: JSON.stringify(sendRequest) },
      });

      setShowShareModal(false);
      setSelectedReport(null);
      GamifyToast.success('Report sent successfully!');
    } catch (err) {
      console.error('Error sharing report:', err);
      GamifyToast.error('Failed to send report. Please try again.');
    } finally {
      setIsSendingReport(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      ' at ' +
      date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
  };

  const renderHeader = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
      }}
    >
      <Typography sx={{ fontSize: 18, fontWeight: 600, color: theme.text }}>
        Reports
      </Typography>
    </Box>
  );

  const renderLoading = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 5,
      }}
    >
      <CircularProgress size={32} sx={{ color: theme.primary }} />
      <Typography sx={{ mt: 2, color: theme.textLight, textAlign: 'center' }}>
        Loading reports...
      </Typography>
    </Box>
  );

  const renderError = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 5,
      }}
    >
      <CloseIcon sx={{ fontSize: 40, color: '#FF0000', mb: 2 }} />
      <Typography sx={{ color: theme.text, textAlign: 'center' }}>
        Error loading reports
      </Typography>
      <Typography
        sx={{
          mt: 1,
          color: theme.textLight,
          textAlign: 'center',
          fontSize: 14,
        }}
      >
        {error}
      </Typography>
      <Button
        onClick={fetchReports}
        variant="contained"
        sx={{
          mt: 2,
          backgroundColor: theme.primary,
          '&:hover': { backgroundColor: theme.primaryDark },
        }}
      >
        Retry
      </Button>
    </Box>
  );

  const renderEmpty = () => (
    <Box
      onClick={fetchReports}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 5,
        cursor: 'pointer',
        '&:hover': { backgroundColor: theme.background2 },
        borderRadius: 2,
      }}
    >
      <DescriptionIcon sx={{ fontSize: 40, color: theme.textLight, mb: 2 }} />
      <Typography sx={{ color: theme.text, textAlign: 'center' }}>
        No reports yet
      </Typography>
      <Typography
        sx={{
          mt: 1,
          color: theme.textLight,
          textAlign: 'center',
          fontSize: 14,
        }}
      >
        Press to check again
      </Typography>
    </Box>
  );

  const renderReportItem = (report: Report, index: number) => (
    <Box
      key={report.id || index}
      onClick={() => handleViewReport(report)}
      sx={{
        backgroundColor: theme.background2,
        borderRadius: 2,
        p: 2,
        mb: 1.5,
        borderLeft: `4px solid ${theme.primary}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: theme.background3,
          transform: 'translateX(4px)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, color: theme.text, mb: 0.5 }}>
            {report.description || 'Untitled Report'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.textLight, mb: 0.5 }}>
            {formatDate(report.createdAt)}
          </Typography>
          {report.version && (
            <Box
              sx={{
                backgroundColor: `${theme.primary}20`,
                borderRadius: 1,
                px: 1,
                py: 0.25,
                display: 'inline-block',
                mt: 0.5,
              }}
            >
              <Typography sx={{ fontSize: 10, color: theme.primary }}>
                v{report.version}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleExportReport(report);
            }}
            size="small"
            sx={{ color: theme.text, mr: 1 }}
          >
            <ShareIcon fontSize="small" />
          </IconButton>
          <ChevronRightIcon sx={{ color: theme.textLight }} />
        </Box>
      </Box>
    </Box>
  );

  const renderReportsList = () => (
    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
      {reports.map((report, index) => renderReportItem(report, index))}
    </Box>
  );

  const renderShareModal = () => (
    <Dialog
      open={showShareModal}
      onClose={() => setShowShareModal(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.background1,
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ color: theme.text }}>Share Report</DialogTitle>
      <DialogContent>
        <FormControlLabel
          control={
            <Checkbox
              checked={shareData.sendToPhone}
              onChange={(e) =>
                setShareData({ ...shareData, sendToPhone: e.target.checked })
              }
              sx={{ color: theme.primary }}
            />
          }
          label={
            <Typography sx={{ color: theme.text }}>Send via SMS</Typography>
          }
        />
        {shareData.sendToPhone && (
          <TextField
            fullWidth
            label="Phone Number"
            value={shareData.phoneNumber}
            onChange={(e) =>
              setShareData({ ...shareData, phoneNumber: e.target.value })
            }
            sx={{ mb: 2, mt: 1 }}
            size="small"
          />
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={shareData.sendToEmail}
              onChange={(e) =>
                setShareData({ ...shareData, sendToEmail: e.target.checked })
              }
              sx={{ color: theme.primary }}
            />
          }
          label={
            <Typography sx={{ color: theme.text }}>Send via Email</Typography>
          }
        />
        {shareData.sendToEmail && (
          <TextField
            fullWidth
            label="Email Address"
            value={shareData.email}
            onChange={(e) =>
              setShareData({ ...shareData, email: e.target.value })
            }
            sx={{ mt: 1 }}
            size="small"
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={() => setShowShareModal(false)}
          sx={{ color: theme.text }}
          disabled={isSendingReport}
        >
          Cancel
        </Button>
        <Button
          onClick={handleShareSend}
          variant="contained"
          disabled={
            isSendingReport ||
            (!shareData.sendToPhone && !shareData.sendToEmail)
          }
          sx={{
            backgroundColor: theme.primary,
            '&:hover': { backgroundColor: theme.primaryDark },
          }}
        >
          {isSendingReport ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : (
            'Send'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const handleShareFromView = () => {
    if (selectedReport) {
      setShowReportView(false);
      handleExportReport(selectedReport);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {renderHeader()}
      {isLoading && renderLoading()}
      {error && !isLoading && renderError()}
      {!isLoading && !error && reports.length === 0 && renderEmpty()}
      {!isLoading && !error && reports.length > 0 && renderReportsList()}
      {renderShareModal()}
      <ReportViewModal
        open={showReportView}
        onClose={() => {
          setShowReportView(false);
          setReportData(null);
        }}
        reportData={reportData}
        onShare={handleShareFromView}
      />
    </Box>
  );
};

export default DealReports;
